import type { EcaFeatureIcon } from "./ecaModuleIcons";

export interface ModuleFeature {
  title: string;
  icon: EcaFeatureIcon;
  description?: string;
  items?: string[];
}

export interface EcaModule {
  id: string;
  title: string;
  description: string;
  features: ModuleFeature[];
}

export const ecaModules: EcaModule[] = [
  {
    id: "customer",
    title: "Customer App Features",
    description:
      "A feature-rich shopping experience for mobile and web — discovery, cart, checkout, tracking, and loyalty in one app.",
    features: [
      {
        title: "Register & Login",
        icon: "login",
        description:
          "Sign up via email, phone, or social accounts with OTP verification and secure password recovery.",
      },
      {
        title: "User Profile",
        icon: "profile",
        description:
          "Manage personal details, saved addresses, payment methods, and notification preferences.",
      },
      {
        title: "Advanced Search & Filters",
        icon: "search",
        description:
          "Search products by keyword, brand, price range, rating, and attributes with smart sort options.",
      },
      {
        title: "Categories & Listings",
        icon: "categories",
        description:
          "Browse detailed categories, sub-categories, and curated product collections with banner promotions.",
      },
      {
        title: "Product Details",
        icon: "product-details",
        description:
          "View high-quality images, variants, specifications, stock status, and vendor information.",
      },
      {
        title: "Wishlist",
        icon: "wishlist",
        description:
          "Save favourite products for later and receive alerts when prices drop or items are back in stock.",
      },
      {
        title: "Shopping Cart",
        icon: "cart",
        description:
          "Add, update, and remove items with quantity controls, variant selection, and saved cart persistence.",
      },
      {
        title: "Easy Checkout",
        icon: "checkout",
        description:
          "Streamlined checkout with address selection, delivery options, and order summary review.",
      },
      {
        title: "Payment Gateway",
        icon: "payment",
        description:
          "Pay via UPI, cards, net banking, wallets, and COD with PCI-compliant secure transactions.",
      },
      {
        title: "Order History",
        icon: "order-history",
        description:
          "Access complete order history with invoices, reorder options, and downloadable receipts.",
      },
      {
        title: "Order Tracking",
        icon: "tracking",
        description:
          "Real-time shipment tracking with status updates, estimated delivery, and courier details.",
      },
      {
        title: "Cancel & Re-order",
        icon: "cancel-reorder",
        description:
          "Cancel eligible orders before dispatch and re-order previous purchases with one tap.",
      },
      {
        title: "Reviews & Ratings",
        icon: "reviews",
        description:
          "Rate products and vendors, upload photos, and read verified buyer reviews before purchase.",
      },
      {
        title: "Offers & Coupons",
        icon: "offers",
        description:
          "Apply promo codes, flash sale discounts, and loyalty rewards at checkout automatically.",
      },
      {
        title: "Push Notifications",
        icon: "notifications",
        description:
          "Receive alerts for order updates, offers, abandoned cart reminders, and new arrivals.",
      },
      {
        title: "Live Chat Support",
        icon: "chat",
        description:
          "In-app chat with support agents for product queries, order issues, and return assistance.",
      },
      {
        title: "Multi-Currency & Language",
        icon: "multi-currency",
        description:
          "Shop in preferred language and currency with automatic conversion for international buyers.",
      },
      {
        title: "Help Desk",
        icon: "help-desk",
        description:
          "Raise tickets, browse FAQs, and track resolution status for returns, refunds, and complaints.",
      },
    ],
  },
  {
    id: "vendor",
    title: "Vendor Panel Features",
    description:
      "Self-service portal for sellers and store owners to manage catalog, orders, promotions, and earnings.",
    features: [
      {
        title: "Vendor Dashboard",
        icon: "dashboard",
        description:
          "Real-time overview of sales, orders, revenue, top products, and pending actions at a glance.",
      },
      {
        title: "Profile Management",
        icon: "vendor-profile",
        description:
          "Update store profile, business documents, bank details, and verification status.",
      },
      {
        title: "Product Management",
        icon: "product-mgmt",
        description:
          "Add, edit, and bulk-upload products with images, variants, pricing, and SEO metadata.",
      },
      {
        title: "Inventory Management",
        icon: "inventory",
        description:
          "Track stock levels, low-stock alerts, warehouse mapping, and automatic out-of-stock rules.",
      },
      {
        title: "Order Management",
        icon: "order-mgmt",
        description:
          "Accept, process, pack, and dispatch orders with status updates and packing slip generation.",
      },
      {
        title: "Cancellation Management",
        icon: "cancellation",
        description:
          "Handle customer cancellation requests, partial cancellations, and refund initiation workflows.",
      },
      {
        title: "Offers & Coupons",
        icon: "vendor-offers",
        description:
          "Create store-level discounts, coupon codes, bundle deals, and time-bound flash sales.",
      },
      {
        title: "Banner Management",
        icon: "banners",
        description:
          "Upload promotional banners and featured product carousels for your storefront.",
      },
      {
        title: "Review Management",
        icon: "review-mgmt",
        description:
          "Respond to customer reviews, flag inappropriate content, and monitor seller ratings.",
      },
      {
        title: "Transaction History",
        icon: "transactions",
        description:
          "View all completed transactions, refunds, chargebacks, and settlement records.",
      },
      {
        title: "Payout & Finance",
        icon: "payout",
        description:
          "Track commission deductions, pending payouts, and download financial statements.",
      },
      {
        title: "Insights & Reports",
        icon: "analytics",
        description:
          "Analyse sales trends, conversion rates, return ratios, and customer demographics.",
      },
    ],
  },
  {
    id: "admin",
    title: "Admin Dashboard Features",
    description:
      "Central control panel to manage the entire marketplace — users, vendors, catalog, payments, and growth.",
    features: [
      {
        title: "Admin Authentication",
        icon: "auth",
        description:
          "Role-based access control for super admins, operations staff, finance teams, and support agents.",
      },
      {
        title: "One-Click Dashboard",
        icon: "admin-dashboard",
        description:
          "Unified dashboard with GMV, orders, active users, vendor signups, and revenue metrics.",
      },
      {
        title: "User Management",
        icon: "user-mgmt",
        description:
          "Manage customer accounts, block fraudulent users, and view purchase behaviour analytics.",
      },
      {
        title: "Vendor Management",
        icon: "vendor-mgmt",
        description:
          "Onboard vendors, approve KYC documents, set commission rates, and suspend non-compliant stores.",
      },
      {
        title: "Category & Brand Management",
        icon: "brand-mgmt",
        description:
          "Create category trees, brand directories, attribute sets, and taxonomy for product classification.",
      },
      {
        title: "Product Oversight",
        icon: "product-oversight",
        description:
          "Moderate product listings, approve new uploads, and enforce marketplace content policies.",
      },
      {
        title: "Order Management",
        icon: "admin-orders",
        description:
          "Monitor all platform orders, intervene in disputes, and manage escalations across vendors.",
      },
      {
        title: "Commission & Payouts",
        icon: "commission",
        description:
          "Configure commission rules, process vendor payouts, and reconcile platform earnings.",
      },
      {
        title: "Offers & Promotions",
        icon: "promotions",
        description:
          "Launch platform-wide sales events, coupon campaigns, and referral programmes.",
      },
      {
        title: "Payment Gateway Settings",
        icon: "gateway",
        description:
          "Configure payment providers, transaction fees, refund policies, and settlement schedules.",
      },
      {
        title: "Shipping & Logistics",
        icon: "shipping",
        description:
          "Manage courier integrations, shipping zones, delivery charges, and return policies.",
      },
      {
        title: "Email & SMS Campaigns",
        icon: "campaigns",
        description:
          "Send promotional blasts, transactional messages, and segmented marketing campaigns.",
      },
      {
        title: "CMS & Content",
        icon: "cms",
        description:
          "Manage homepage banners, static pages, blogs, and SEO content without developer help.",
      },
      {
        title: "Reports & Analytics",
        icon: "reports",
        description:
          "Export sales, tax, vendor performance, and customer acquisition reports for business decisions.",
      },
    ],
  },
];

export const ecaAdvantages = [
  "Complete e-commerce app for single stores, multi-vendor marketplaces, and D2C brands",
  "Customer, vendor, and admin panels integrated in one scalable platform",
  "Shopify, WooCommerce, and custom marketplace clone with native mobile apps",
  "Secure payment gateway integration — UPI, cards, wallets, and cash on delivery",
  "Advanced search, filters, wishlist, cart, and one-click checkout experience",
  "Real-time order tracking, notifications, and live chat customer support",
  "Vendor self-service for products, inventory, orders, payouts, and analytics",
  "Commission management, promotions, and campaign tools for marketplace operators",
  "Multi-currency, multi-language, and SEO-ready architecture for global selling",
  "Cloud-hosted with responsive web and native iOS/Android app deployment",
];

export const ecaWorkflowSteps = [
  {
    title: "Store Setup",
    text: "Admin configures categories, payment gateways, shipping rules, and onboards vendors with KYC verification.",
  },
  {
    title: "Catalog Upload",
    text: "Vendors add products with images, variants, pricing, and inventory while admin moderates listings.",
  },
  {
    title: "Customer Discovery",
    text: "Shoppers browse, search, filter, and add products to cart or wishlist across web and mobile apps.",
  },
  {
    title: "Checkout & Payment",
    text: "Customers apply coupons, select delivery options, and pay securely through integrated gateways.",
  },
  {
    title: "Order Fulfilment",
    text: "Vendors process orders, generate packing slips, and dispatch via integrated logistics partners.",
  },
  {
    title: "Delivery & Feedback",
    text: "Customers track shipments, confirm delivery, leave reviews, and vendors receive payouts after settlement.",
  },
];

export const ecaPlatformApps = [
  {
    title: "Customer App",
    description:
      "Browse, search, cart, checkout, track orders, apply coupons, and manage profile — available on web, iOS, and Android.",
    icon: "customer",
  },
  {
    title: "Vendor Panel",
    description:
      "Manage products, inventory, orders, offers, banners, reviews, transactions, and business analytics.",
    icon: "vendor",
  },
  {
    title: "Admin Dashboard",
    description:
      "Control users, vendors, catalog, commissions, promotions, payments, shipping, and platform-wide reports.",
    icon: "admin",
  },
  {
    title: "Seller Mobile App",
    description:
      "Vendors manage orders on the go with push alerts, quick status updates, and mobile sales insights.",
    icon: "seller-app",
  },
];

export const ecaIndustries = [
  "Fashion & Apparel",
  "Electronics",
  "Food & Grocery",
  "Healthcare",
  "Furniture",
  "Beauty & Cosmetics",
  "Automotive",
  "Education",
];
